import {ToolExecuteParams} from 'common/src/modules/shared/copilot/copilot.types';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import {CLStore} from 'common/src/redux/store';
import {V2CardAgentToolName} from '../../promptToCardAgentV2.enums';
import {ContentCardScratchpadState} from '../../scratchpad/contentCardScratchpad.types';
import preProcessStandaloneAgent from '../../utils/general/preProcessStandaloneAgent';
import {addOrUpdateCardInputSchema} from '../../../../schemas/baseSchemas';
import standaloneAgent from 'common/src/modules/shared/copilot/utils/creators/standaloneAgent';
import {NodeType} from 'common/src/modules/nodes/nodes.types';
import getPromptToCardAgentInstructions from '../../../promptToCardAgent/utils/getPromptToCardAgentInstructions';
import {cardTypeToName} from '../../../../campaignSetupAgent.config';
import setCardNameTool from '../general/setCardNameTool';
import setCardDescriptionTool from '../general/setCardDescriptionTool';
import getCardDetailsTool from '../general/getCardDetailsTool';
import {responseSchema} from '../../promptToCardAgentV2.schemas';
import getSetupAgentModel from '../../../../utils/getSetupAgentModel';
import getOrCreateContentCard from '../../utils/content/getOrCreateContentCard';
import postProcessStandaloneAgent from '../../utils/general/postProcessStandaloneAgent';
import askAboutObjectsTool from '../../../../tools/general/askAboutObjectsTool/askAboutObjectsTool';
import {ContentSelectionStrategyValue} from 'common/src/modules/contentFeeds/contentFeeds.enums';
import setContentStrategyTool from './setContentStrategyTool';
import setContentFeedTool from './setContentFeedTool';
import setContentSetTool from './setContentSetTool';
import setSpecificContentTool from './setSpecificContentTool';
import searchContentFeedColumnsTool from './searchContentFeedColumnsTool';
import searchContentSignalsTool from './searchContentSignalsTool';
import setMatchingConditionsTool from './setMatchingConditionsTool';
const cardSpecificInstructions = `- Use the ${V2CardAgentToolName.SearchContentFeeds} tool to search for content feeds when the user doesn't provide a specific feed ID.
- Use the ${V2CardAgentToolName.SetContentStrategy} tool to set the content selection strategy (Specific or Matching).
- If the strategy is ${ContentSelectionStrategyValue.Specific}, use the ${V2CardAgentToolName.SetSpecificContent} tool to set the specific content ID.
- If the strategy is ${ContentSelectionStrategyValue.Matching}, use the ${V2CardAgentToolName.SearchContentFeedColumns} and ${V2CardAgentToolName.SearchContentSignals} tools to search for the best feed columns and signals, following the user's request. Do not try to search for the signals NOR columns using any tool other than the ones provided here.
- Use the ${V2CardAgentToolName.SetContentFeed} tool to set the content feed ID when provided.
- Use the ${V2CardAgentToolName.SetContentSet} tool to set the content set ID when provided. If setting a content set, you must also set the feed ID to match the content set's feed ID.`;

export default function addOrUpdateContentCardToolV2(store: CLStore) {
  async function execute(
    params: ToolExecuteParams<typeof addOrUpdateCardInputSchema, ContentCardScratchpadState>,
  ) {
    const {toolUtils, callChain} = params;

    const {promptToUse, normalizedCardId: cardId} = preProcessStandaloneAgent(params, store);

    const standaloneAgentDef = standaloneAgent({
      name: V2CardAgentToolName.AddOrUpdateContentCard,
      role: `Your purpose is to convert a natural language prompt to a ${
        cardTypeToName[NodeType.CONTENT_FEED]
      } card`,
      instructions: getPromptToCardAgentInstructions({
        cardType: NodeType.CONTENT_FEED,
        cardId,
        cardSpecificInstructions,
      }),
      tools: [
        setCardNameTool(),
        setCardDescriptionTool(),
        getCardDetailsTool(),
        askAboutObjectsTool(store),

        // Content tools
        setContentStrategyTool(),
        setContentFeedTool(),
        setContentSetTool(),
        setSpecificContentTool(),
        searchContentFeedColumnsTool(),
        searchContentSignalsTool(store),
        setMatchingConditionsTool(store),
      ],
      responseSchema,
      model: getSetupAgentModel(),
    });

    const toolScratchpad = {card: await getOrCreateContentCard(cardId, store)};

    const result = await toolUtils.invokeStandaloneAgent({
      agent: standaloneAgentDef,
      prompt: promptToUse,
      callChain,
      scratchpad: toolScratchpad,
    });

    return postProcessStandaloneAgent({
      result: {status: result.status, chainOfThoughts: result.chainOfThoughts},
      scratchpad: toolScratchpad,
      store,
      cardId,
    });
  }

  return tool({
    name: V2CardAgentToolName.AddOrUpdateContentCard,
    displayName: 'Working on content card',
    description:
      'Add or update a content selection card. The card selects a single content item from content feeds to inject into ad context for creative display or decisioning. ' +
      'Can configure content selection strategy, configure matching conditions for matching strategy, assign a feed, ' +
      'assign a content set, or assign a specific content item for the specific items strategy.',
    execute,
    inputSchema: addOrUpdateCardInputSchema,
  });
}
